import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, UserPlus, Mail, Phone, Lock, User, Briefcase, Eye, EyeOff, CheckSquare, Square } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { staffService } from '../../services/staffService';
import { customToast } from '../../utils/customToast';
import { StaffRoleType, AvailableModule, CreateStaffRequest } from '../../types';

interface AddStaffModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const AddStaffModal: React.FC<AddStaffModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const { theme } = useTheme();
    const [loading, setLoading] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [availableModules, setAvailableModules] = useState<AvailableModule[]>([]);
    const [loadingModules, setLoadingModules] = useState(true);

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        full_name: '',
        phone: '',
        staff_role_type: '' as StaffRoleType,
    });

    const [permissions, setPermissions] = useState<Record<string, {
        can_view: boolean;
        can_create: boolean;
        can_edit: boolean;
        can_delete: boolean;
    }>>({});

    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (isOpen) {
            loadModules();
        }
    }, [isOpen]);

    const loadModules = async () => {
        try {
            setLoadingModules(true);
            const modules = await staffService.getAvailableModules();
            setAvailableModules(modules);

            // Initialize permissions
            const initialPermissions: Record<string, any> = {};
            modules.forEach(mod => {
                initialPermissions[mod.key] = {
                    can_view: false,
                    can_create: false,
                    can_edit: false,
                    can_delete: false,
                };
            });
            setPermissions(initialPermissions);
        } catch (error) {
            console.error('Failed to load modules:', error);
            customToast.error('Failed to load permission modules');
        } finally {
            setLoadingModules(false);
        }
    };

    if (!isOpen) return null;

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handlePermissionChange = (module: string, permission: string, value: boolean) => {
        setPermissions(prev => ({
            ...prev,
            [module]: {
                ...prev[module],
                [permission]: value,
                // If enabling create/edit/delete, also enable view
                ...(permission !== 'can_view' && value ? { can_view: true } : {}),
                // If disabling view, disable all others
                ...(permission === 'can_view' && !value ? { can_create: false, can_edit: false, can_delete: false } : {}),
            },
        }));
    };

    const handleSelectAllForModule = (module: string, allSelected: boolean) => {
        setPermissions(prev => ({
            ...prev,
            [module]: {
                can_view: !allSelected,
                can_create: !allSelected,
                can_edit: !allSelected,
                can_delete: !allSelected,
            },
        }));
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.username.trim()) newErrors.username = 'Username is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
        if (!formData.password) newErrors.password = 'Password is required';
        else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
        if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';

        // Check if at least one permission is granted
        const hasPermission = Object.values(permissions).some(p => p.can_view);
        if (!hasPermission) newErrors.permissions = 'Please grant at least one module access';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            setLoading(true);

            const permissionsArray = Object.entries(permissions)
                .filter(([_, p]) => p.can_view || p.can_create || p.can_edit || p.can_delete)
                .map(([module, p]) => ({
                    module,
                    can_view: p.can_view,
                    can_create: p.can_create,
                    can_edit: p.can_edit,
                    can_delete: p.can_delete,
                }));

            const request: CreateStaffRequest = {
                ...formData,
                permissions: permissionsArray,
            };

            await staffService.createStaff(request);
            customToast.success('Staff member added successfully');
            onSuccess();
            handleClose();
        } catch (error: any) {
            console.error('Error creating staff:', error);
            const message = error.response?.data?.message || 'Failed to create staff member';
            customToast.error(message);

            // Handle validation errors from backend
            if (error.response?.data?.errors) {
                const backendErrors: Record<string, string> = {};
                Object.entries(error.response.data.errors).forEach(([key, msgs]: [string, any]) => {
                    backendErrors[key] = Array.isArray(msgs) ? msgs[0] : msgs;
                });
                setErrors(backendErrors);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            setFormData({
                username: '',
                email: '',
                password: '',
                full_name: '',
                phone: '',
                staff_role_type: '' as StaffRoleType,
            });
            setPermissions({});
            setErrors({});
            onClose();
        }, 150);
    };

    const roleOptions: { value: StaffRoleType; label: string; description: string }[] = [
        { value: 'manager', label: 'Manager', description: 'Full operational access' },
        { value: 'seller', label: 'Seller', description: 'Sales and POS access' },
        { value: 'worker', label: 'Worker', description: 'Limited access' },
    ];

    return createPortal(
        <div className={`fixed inset-0 z-50 overflow-y-auto ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}>
            <div className="flex items-center justify-center min-h-screen p-4">
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                    onClick={handleClose}
                />

                <div className={`relative w-full max-w-2xl overflow-hidden text-left transform shadow-2xl rounded-lg ${isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
                    } ${theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
                    {/* Header */}
                    <div className={`px-4 sm:px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-indigo-900/50' : 'bg-indigo-100'
                                    }`}>
                                    <UserPlus className={`w-5 h-5 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'
                                        }`} />
                                </div>
                                <div>
                                    <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        Add New Staff Member
                                    </h2>
                                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Create account and set permissions
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleClose}
                                className={`p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'
                                    }`}
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="px-4 sm:px-6 py-4 max-h-[calc(100vh-16rem)] sm:max-h-[60vh] overflow-y-auto">
                            {/* Basic Info Section */}
                            <div className="space-y-4 mb-6">
                                <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Account Information
                                </h3>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Username */}
                                    <div>
                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            Username *
                                        </label>
                                        <div className="relative">
                                            <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                            <input
                                                type="text"
                                                value={formData.username}
                                                onChange={(e) => handleInputChange('username', e.target.value)}
                                                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${errors.username ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                                placeholder="staff_user"
                                            />
                                        </div>
                                        {errors.username && <p className="mt-1 text-sm text-red-500">{errors.username}</p>}
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            Email *
                                        </label>
                                        <div className="relative">
                                            <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                            <input
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${errors.email ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                                placeholder="staff@example.com"
                                            />
                                        </div>
                                        {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                                    </div>

                                    {/* Full Name */}
                                    <div>
                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            Full Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.full_name}
                                            onChange={(e) => handleInputChange('full_name', e.target.value)}
                                            className={`w-full px-4 py-2 rounded-lg border ${errors.full_name ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                            placeholder="John Doe"
                                        />
                                        {errors.full_name && <p className="mt-1 text-sm text-red-500">{errors.full_name}</p>}
                                    </div>

                                    {/* Phone */}
                                    <div>
                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            Phone
                                        </label>
                                        <div className="relative">
                                            <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                            <input
                                                type="tel"
                                                value={formData.phone}
                                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                                placeholder="+92 300 1234567"
                                            />
                                        </div>
                                    </div>

                                    {/* Password */}
                                    <div>
                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            Password *
                                        </label>
                                        <div className="relative">
                                            <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={formData.password}
                                                onChange={(e) => handleInputChange('password', e.target.value)}
                                                className={`w-full pl-10 pr-10 py-2 rounded-lg border ${errors.password ? 'border-red-500' : theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                                placeholder="Min 6 characters"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                                            >
                                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                        {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
                                    </div>

                                    {/* Role Type */}
                                    <div>
                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            Role Type *
                                        </label>
                                        <div className="relative">
                                            <Briefcase className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                            <select
                                                value={formData.staff_role_type}
                                                onChange={(e) => handleInputChange('staff_role_type', e.target.value)}
                                                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                            >
                                                <option value="">Select Role Type</option>
                                                {roleOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Permissions Section */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Module Permissions
                                    </h3>
                                    {errors.permissions && <p className="text-sm text-red-500">{errors.permissions}</p>}
                                </div>

                                {loadingModules ? (
                                    <div className="flex items-center justify-center py-8">
                                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                ) : (
                                    <div className={`border rounded-lg overflow-hidden ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                                        <table className="w-full">
                                            <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                                                <tr>
                                                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Module</th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>View</th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Create</th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Edit</th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Delete</th>
                                                    <th className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>All</th>
                                                </tr>
                                            </thead>
                                            <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                                {availableModules.map((mod) => {
                                                    const perm = permissions[mod.key] || { can_view: false, can_create: false, can_edit: false, can_delete: false };
                                                    const allSelected = perm.can_view && perm.can_create && perm.can_edit && perm.can_delete;

                                                    return (
                                                        <tr key={mod.key} className={theme === 'dark' ? 'hover:bg-gray-750' : 'hover:bg-gray-50'}>
                                                            <td className={`px-4 py-3 text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>{mod.name}</td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePermissionChange(mod.key, 'can_view', !perm.can_view)}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                                                    title="View permission"
                                                                >
                                                                    {perm.can_view ? (
                                                                        <CheckSquare size={20} className="text-blue-600 dark:text-blue-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePermissionChange(mod.key, 'can_create', !perm.can_create)}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                                                    title="Create permission"
                                                                >
                                                                    {perm.can_create ? (
                                                                        <CheckSquare size={20} className="text-green-600 dark:text-green-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePermissionChange(mod.key, 'can_edit', !perm.can_edit)}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                                                    title="Edit permission"
                                                                >
                                                                    {perm.can_edit ? (
                                                                        <CheckSquare size={20} className="text-amber-600 dark:text-amber-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePermissionChange(mod.key, 'can_delete', !perm.can_delete)}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                                                    title="Delete permission"
                                                                >
                                                                    {perm.can_delete ? (
                                                                        <CheckSquare size={20} className="text-red-600 dark:text-red-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                            <td className="px-2 py-3 text-center">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSelectAllForModule(mod.key, allSelected)}
                                                                    className="inline-flex items-center justify-center p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                                                    title="Select all permissions for this module"
                                                                >
                                                                    {allSelected ? (
                                                                        <CheckSquare size={20} className="text-green-600 dark:text-green-400" />
                                                                    ) : (
                                                                        <Square size={20} className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} />
                                                                    )}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className={`px-4 sm:px-6 py-4 border-t flex flex-col-reverse sm:flex-row space-y-reverse space-y-2 sm:space-y-0 sm:space-x-3 ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
                            }`}>
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className={`w-full sm:flex-1 px-4 py-2.5 sm:py-2 text-sm font-medium rounded-lg border transition-colors ${theme === 'dark'
                                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || loadingModules}
                                className={`w-full sm:flex-1 px-4 py-2.5 sm:py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                {loading ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : null}
                                <span>{loading ? 'Creating...' : 'Add Staff Member'}</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AddStaffModal;
